import { Component, Input, forwardRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { FormErrorComponent } from '../form-error/form-error.component';

/** Informações de um arquivo na lista */
export interface MultiFileInfo {
  file: File;
  name: string;
  size: string;
  icon: string;
}

/**
 * IsMultiFileFieldComponent
 *
 * Campo de upload múltiplo com drag & drop, lista de arquivos e remoção individual.
 * Emite um array de Files como valor do formulário.
 *
 * @example
 * <is-multi-file-field
 *   label="Anexos"
 *   accept=".pdf,.jpg,.png"
 *   [maxFiles]="5"
 *   [maxSizeMb]="10"
 *   formControlName="attachments"
 *   [control]="form.get('attachments')"
 * ></is-multi-file-field>
 */
@Component({
  selector: 'is-multi-file-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe, FormErrorComponent],
  templateUrl: './multi-file-field.component.html',
  styleUrls: ['./multi-file-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiFileFieldComponent),
      multi: true,
    },
  ],
})
export class MultiFileFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() accept = '';
  @Input() required = false;
  @Input() helpText = '';
  @Input() control: AbstractControl | null = null;
  @Input() maxSizeMb = 10;
  @Input() maxFiles = 10;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  files: MultiFileInfo[] = [];
  isDragging = false;
  isDisabled = false;

  private onChange: (val: File[]) => void = () => {};
  private onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  /** Formatos aceitos legíveis */
  get acceptedFormatsLabel(): string {
    if (!this.accept) return '';
    return this.accept
      .split(',')
      .map((ext) => ext.trim().replace('.', '').toUpperCase())
      .join(', ');
  }

  /** Quantidade restante de arquivos permitidos */
  get remainingSlots(): number {
    return this.maxFiles - this.files.length;
  }

  /** Se atingiu o limite de arquivos */
  get isMaxReached(): boolean {
    return this.files.length >= this.maxFiles;
  }

  writeValue(_val: any): void {
    this.files = [];
  }

  registerOnChange(fn: (val: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  /** Abre o seletor de arquivo nativo */
  openFilePicker(): void {
    if (!this.isDisabled && !this.isMaxReached) {
      this.fileInput.nativeElement.click();
    }
  }

  /** Processa arquivos selecionados via input */
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const fileList = input.files;
    if (fileList) {
      this.addFiles(Array.from(fileList));
    }
    // Reset input para permitir selecionar o mesmo arquivo novamente
    input.value = '';
  }

  /** Drag over */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isDisabled && !this.isMaxReached) {
      this.isDragging = true;
    }
  }

  /** Drag leave */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  /** Drop */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (this.isDisabled || this.isMaxReached) return;

    const fileList = event.dataTransfer?.files;
    if (fileList) {
      this.addFiles(Array.from(fileList));
    }
  }

  /** Remove um arquivo da lista pelo índice */
  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.emitValue();
  }

  /** Remove todos os arquivos */
  clearAll(): void {
    this.files = [];
    this.emitValue();
  }

  // ─── Privados ──────────────────────────────────────────────────────────────

  private addFiles(newFiles: File[]): void {
    const available = this.maxFiles - this.files.length;
    const toAdd = newFiles.slice(0, available);

    for (const file of toAdd) {
      // Evita duplicatas pelo nome
      if (this.files.some((f) => f.name === file.name && f.file.size === file.size)) {
        continue;
      }

      this.files.push({
        file,
        name: file.name,
        size: this.formatFileSize(file.size),
        icon: this.getFileIcon(file.name),
      });
    }

    this.emitValue();
    this.onTouched();
  }

  private emitValue(): void {
    this.onChange(this.files.map((f) => f.file));
  }

  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  private getFileIcon(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const iconMap: Record<string, string> = {
      pdf: 'bxs-file-pdf',
      doc: 'bxs-file-doc',
      docx: 'bxs-file-doc',
      xls: 'bxs-file',
      xlsx: 'bxs-file',
      jpg: 'bxs-file-image',
      jpeg: 'bxs-file-image',
      png: 'bxs-file-image',
      webp: 'bxs-file-image',
      gif: 'bxs-file-image',
      zip: 'bxs-file-archive',
      rar: 'bxs-file-archive',
    };
    return iconMap[ext] || 'bxs-file-blank';
  }
}
